import { getTailwindColor, type TailwindCssColor } from '@salescore/client-base'
import { Tooltip } from 'antd'
import { Number } from 'effect'
import type { CSSProperties, ReactElement, ReactNode } from 'react'

import type { RSheetsNodeBlockCell, RSheetsNodeBlockRow } from '../../../domain/column'
import { RSheetsStyle } from '../../util/RSheetsStyle'

const TOP_RATIO = 0.1

export interface BlockHeaderStyle {
  treeHeaderHeightPerNode: number
  blockStyle: CSSProperties
  renderCellLabel: (cell: RSheetsNodeBlockCell) => ReactNode
}

export const RSheetsNodeBlockHeader = ({
  maxDepth,
  nodeBlockHeader,
  blockHeaderStyle: { blockStyle, treeHeaderHeightPerNode, renderCellLabel },
}: {
  maxDepth: number
  nodeBlockHeader: RSheetsNodeBlockRow[]
  blockHeaderStyle: BlockHeaderStyle
}): ReactElement => {
  if (nodeBlockHeader.length === 0) {
    return (
      <div
        style={{
          height: maxDepth * treeHeaderHeightPerNode,
          width: '100%',
          backgroundColor: RSheetsStyle.color.backgroundColor,
        }}
      />
    )
  }

  return (
    <>
      {nodeBlockHeader.map((headerRow, depth) => (
        <NodeBlockHeaderRow
          row={headerRow}
          height={maxDepth * treeHeaderHeightPerNode}
          top={depth * treeHeaderHeightPerNode * (1 - TOP_RATIO)}
          key={depth}
          blockStyle={blockStyle}
          renderCellLabel={renderCellLabel}
        />
      ))}
    </>
  )
}

const NodeBlockHeaderRow = ({
  row,
  height,
  top,
  blockStyle,
  renderCellLabel,
}: {
  row: RSheetsNodeBlockRow
  height: number
  top: number
  blockStyle: CSSProperties
  renderCellLabel: (cell: RSheetsNodeBlockCell) => ReactNode
}): ReactElement => (
  <div
    style={{
      width: Number.sumAll(row.map((x) => x.width)),
      height,
      top,
    }}
    className="absolute flex overflow-hidden"
  >
    {row.map((cell, index) => (
      <Block cell={cell} key={index} blockStyle={blockStyle} cellLabelRender={renderCellLabel} />
    ))}
    {/* eslint-disable-next-line sonarjs/todo-tag */}
    {/* TODO: stickyっぽく表示が必要。スクロールをハンドリングするしかなさそう */}
  </div>
)

const Block = ({
  cell,
  blockStyle,
  cellLabelRender,
}: {
  cell: RSheetsNodeBlockCell
  blockStyle: CSSProperties
  cellLabelRender: (cell: RSheetsNodeBlockCell) => ReactNode
}): ReactElement => (
  <div
    style={{
      position: 'sticky',
      left: 0,
      width: cell.width,
      padding: 4,
      margin: '0px',
      borderTopRightRadius: 4,
      borderTopLeftRadius: 4,
      ...getHeaderColorStylesWithCell(cell),
      paddingTop: 5,
      fontSize: 10,
      ...blockStyle,
    }}
  >
    <Tooltip title={cell.tooltipText} placement="right">
      {cellLabelRender(cell)}
    </Tooltip>
    {cell.smallLabel !== undefined && (
      <div
        className="absolute top-0"
        style={{
          fontSize: 10,
          transform: `scale(0.8)`,
          left: 0,
          color: getHeaderColor(cell.color).textColor,
          opacity: 0.7,
        }}
      >
        {cell.smallLabel}
      </div>
    )}
  </div>
)

const getHeaderColorStylesWithCell = (cell: RSheetsNodeBlockCell): CSSProperties => {
  if (cell.label === '-' || cell.color === undefined) {
    return {
      opacity: 0,
    }
  }

  return {
    color: getHeaderColor(cell.color).textColor,
    border: `1px ${getHeaderColor(cell.color).borderColor} solid`,
    backgroundColor: getHeaderColor(cell.color).bgColor,
  }
}

export const getHeaderColor = (color: TailwindCssColor | undefined) => ({
  cellColor: getTailwindColor(color ?? 'gray', 25),
  bgColor: getTailwindColor(color ?? 'gray', 100),
  bgColorDark: getTailwindColor(color ?? 'gray', 500),
  borderColor: getTailwindColor(color ?? 'gray', 200),
  textColor: getTailwindColor(color ?? 'gray', 700),
  textColorWhite: `white`,
})

export const DEFAULT_HEADER_COLOR = getHeaderColor(undefined)
